import { supabase } from "@/integrations/supabase/client";
import type {
  ApiResult,
  BackerCodeValidation,
  BackerRedemptionResult,
  BackerRedemption,
  FoundersWallEntry,
} from "@/types";

export const backerApi = {
  /**
   * Validate a backer claim code (read-only, no activation)
   */
  async validateCode(code: string): Promise<ApiResult<BackerCodeValidation>> {
    try {
      const { data, error } = await supabase.rpc("validate_backer_code", {
        p_code: code,
      });
      if (error) throw error;
      const result = data?.[0];
      if (!result?.is_valid) {
        throw new Error(result?.error_message || "Invalid code");
      }
      return { data: result as BackerCodeValidation, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Redeem a validated code — atomic activation of all rewards
   */
  async redeemCode(
    code: string,
    userId: string
  ): Promise<ApiResult<BackerRedemptionResult>> {
    try {
      const { data, error } = await supabase.rpc("redeem_backer_code", {
        p_code: code,
        p_user_id: userId,
      });
      if (error) throw error;
      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.error_message || "Redemption failed");
      }
      return { data: result as BackerRedemptionResult, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get current user's redeemed rewards
   */
  async getMyRewards(
    userId: string
  ): Promise<ApiResult<BackerRedemption[]>> {
    try {
      const { data, error } = await supabase.rpc("get_backer_rewards", {
        p_user_id: userId,
      });
      if (error) throw error;
      return { data: (data as BackerRedemption[]) || [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get founders wall public entries
   */
  async getFoundersWall(): Promise<ApiResult<FoundersWallEntry[]>> {
    try {
      const { data, error } = await supabase.rpc("get_founders_wall");
      if (error) throw error;
      return { data: (data as FoundersWallEntry[]) || [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Use one bounty post credit (called during bounty posting)
   */
  async usePostCredit(
    userId: string
  ): Promise<ApiResult<{ credits_remaining: number }>> {
    try {
      const { data, error } = await supabase.rpc("use_bounty_post_credit", {
        p_user_id: userId,
      });
      if (error) throw error;
      const result = data?.[0];
      return {
        data: { credits_remaining: result?.credits_remaining ?? 0 },
        error: null,
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // === Admin Functions ===

  /**
   * Generate new batch of backer codes (admin only)
   */
  async generateCodes(
    codes: Array<{
      tier: string;
      tier_label: string;
      tier_amount_cents: number;
    }>
  ): Promise<ApiResult<string[]>> {
    try {
      const { data, error } = await supabase.rpc(
        "admin_generate_backer_codes",
        { p_codes: codes }
      );
      if (error) throw error;
      return { data: (data as string[]) || [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * List all codes with redemption status (admin only)
   */
  async listAllCodes(): Promise<
    ApiResult<
      Array<{
        id: string;
        code: string;
        tier: string;
        tier_label: string;
        tier_amount_cents: number;
        is_active: boolean;
        times_redeemed: number;
        created_at: string;
        backer_redemptions: Array<{
          user_id: string;
          redeemed_at: string;
        }> | null;
      }>
    >
  > {
    try {
      const { data, error } = await supabase
        .from("backer_codes")
        .select(
          `
          *,
          backer_redemptions (
            user_id,
            redeemed_at
          )
        `
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Toggle a code active/inactive (admin only)
   */
  async toggleCodeActive(
    codeId: string,
    isActive: boolean
  ): Promise<ApiResult<null>> {
    try {
      const { error } = await supabase
        .from("backer_codes")
        .update({ is_active: isActive })
        .eq("id", codeId);
      if (error) throw error;
      return { data: null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};
